import type { ContentTree, SiteContentElement } from '../types/SiteContentElement';

/**
 * Severity levels for merge errors; corresponds to console methods.
 */
type MergeTreeErrorSeverity = 'error' | 'warn';

/**
 * Describes a merge error for mergeTrees.
 */
export interface MergeTreeError {
    /** Severity of the error, must correspond to console methods */
    severity: MergeTreeErrorSeverity;
    /** Full dot-delimited path to the property in question */
    path: string;
    /** What went wrong */
    message: string;
    /** Placeholder value, if any (undefined for unexpected extras) */
    placeholder?: unknown;
    /** The detected kind or type of the actual value */
    actualKind: string;
    /** The actual value found in the loaded tree */
    actualValue: unknown;
}

// Helper to detect leaf content elements
function isSiteContentElement(obj: any): obj is SiteContentElement {
    return obj && typeof obj === 'object' && typeof obj.kind === 'string';
}

// Helper to determine the maximum error severity from an array of errors
export function getMaxErrorSeverity(errors: MergeTreeError[]): MergeTreeErrorSeverity {
    return errors.reduce(
        (max, err): MergeTreeErrorSeverity => (err.severity === 'error' ? 'error' : max),
        'warn' as MergeTreeErrorSeverity
    );
}

/**
 * Merge a placeholder tree with the actual loaded tree, preserving expected structure.
 * For leaf elements, prefer actual values when valid; otherwise fallback to placeholder.
 * For any array-valued node (e.g. "entries"), prefer the loaded array if non-empty.
 *
 * @param placeholder - the default ContentTree structure
 * @param tree        - the actual loaded ContentTree
 * @param errors      - array that will collect error contexts instead of logging immediately
 * @param path        - full property path used as context for error reporting only
 */
export function mergeTrees(
    placeholder: ContentTree,
    tree: ContentTree,
    errors: MergeTreeError[] = [],
    path: string = '' // Used for error reporting only
): ContentTree {
    const result: ContentTree = {};

    // First, handle all placeholder-defined keys
    for (const key of Object.keys(placeholder)) {
        const currentPath = path ? `${path}.${key}` : key;
        const placeholderVal = (placeholder as any)[key];
        const treeVal = (tree as any)?.[key];
        const actualKind =
            treeVal && typeof treeVal === 'object' && 'kind' in treeVal
                ? (treeVal as any).kind
                : typeof treeVal;

        // Special-case arrays (like 'entries'): use real data if it's a non-empty array
        if (Array.isArray(placeholderVal)) {
            if (Array.isArray(treeVal) && treeVal.length > 0) {
                result[key] = treeVal;
            } else {
                errors.push({
                    severity: 'error',
                    path: currentPath,
                    message: 'missing or empty array, using placeholder',
                    placeholder: placeholderVal,
                    actualKind,
                    actualValue: treeVal,
                });
                result[key] = placeholderVal;
            }
        }
        // Helper to detect leaf content elements
        else if (isSiteContentElement(placeholderVal)) {
            if (isSiteContentElement(treeVal) && treeVal.kind === placeholderVal.kind) {
                result[key] = treeVal;
            } else {
                errors.push({
                    severity: 'error',
                    path: currentPath,
                    message: `invalid or missing leaf element (expected kind="${placeholderVal.kind}"), using placeholder`,
                    placeholder: placeholderVal,
                    actualKind,
                    actualValue: treeVal,
                });
                result[key] = placeholderVal;
            }
        }
        // Nested object: recurse
        else if (placeholderVal && typeof placeholderVal === 'object') {
            if (!treeVal || typeof treeVal !== 'object') {
                errors.push({
                    severity: 'error',
                    path: currentPath,
                    message: 'missing or non-object subtree, recursing into placeholder',
                    placeholder: placeholderVal,
                    actualKind,
                    actualValue: treeVal,
                });
            }
            result[key] = mergeTrees(
                placeholderVal as ContentTree,
                treeVal && typeof treeVal === 'object' ? (treeVal as ContentTree) : {},
                errors,
                currentPath
            );
        }
        // Primitives or anything else: just use placeholder
        else {
            errors.push({
                severity: 'error',
                path: currentPath,
                message: 'replacing primitive with placeholder',
                placeholder: placeholderVal,
                actualKind,
                actualValue: treeVal,
            });
            result[key] = placeholderVal;
        }
    }

    // Then detect any extra properties in `tree` not defined in `placeholder`
    for (const key of Object.keys(tree)) {
        if (!(key in placeholder)) {
            const currentPath = path ? `${path}.${key}` : key;
            const treeVal = (tree as any)[key];
            const actualKind =
                treeVal && typeof treeVal === 'object' && 'kind' in treeVal
                    ? (treeVal as any).kind
                    : typeof treeVal;

            errors.push({
                severity: 'warn',
                path: currentPath,
                message: 'unexpected property not defined in frontend placeholder',
                actualKind,
                actualValue: treeVal,
            });
            // Do not include unexpected properties in the result
        }
    }

    return result;
}
